import moment from "moment";
import { emailsSender, MDWorkstationListName, myDocumentsListName, paramsListName, siteUrl } from "src/api/config";
import { listMap } from "src/api/config/systemSwitch";
import { emailQueueListName } from "src/api/config/email.config";
import { notificationIdsListName, notificationsCardListName } from "src/api/config/notifications.config";
import { docStatuses, notificationStatuses } from "src/api/config/systemSwitch";
import { ListFieldsKeys, ListFieldsType } from "src/api/config";
import axios from "axios";
import { nameFromEmail } from "src/utils/users.utils";
import { deleteItemRequest } from "./deleteItemRequest";
import { getItemsRequest } from "./getItemsRequest";
import { strapiUrl } from "src/modules/users/constants";

type WorkstationSyncContext = {
  workstationId: number;
  addedUserEmails: string[];
};

const SYSTEM_AUTHOR_EMAIL = "supply.portal@abinbevefes.com";

function getRequestConfig() {
  const token = sessionStorage.getItem("accessToken");
  const requestConfig: any = { withCredentials: true };
  if (token && token !== "mobile-token") requestConfig.headers = { Authorization: `Bearer ${token}` };
  return requestConfig;
}

async function createRawItem<K extends ListFieldsKeys>(listName: K, body: Record<string, any>) {
  const listStrapiApi = listMap[listName].replace(":folder", "");
  const response = await axios.post(`${strapiUrl}${listStrapiApi}`, { data: body }, getRequestConfig());
  return response.data.data["id"] as number;
}

function normalizeEmail(email: string | null | undefined) {
  return (email || "").toLowerCase().trim();
}

function getLookupId(value: any): number | null {
  if (!value) return null;
  if (typeof value === "number") return value;
  if (typeof value?.ID === "number") return value.ID;
  if (typeof value?.id === "number") return value.id;
  if (typeof value?.get_lookupId === "function") return value.get_lookupId();
  return null;
}

function extractWorkstationUserEmails(users: any[] | undefined) {
  if (!Array.isArray(users)) return [];

  return users
    .map((user) => {
      if (typeof user === "string") return normalizeEmail(user);
      if (typeof user?.Email === "string") return normalizeEmail(user.Email);
      if (typeof user?.email === "string") return normalizeEmail(user.email);
      if (typeof user?.get_email === "function") return normalizeEmail(user.get_email());
      return "";
    })
    .filter(Boolean);
}

function extractUserMeta(users: any[] | undefined) {
  const meta = new Map<string, { name: string; personalNumber: string }>();

  if (!Array.isArray(users)) return meta;

  users.forEach((user) => {
    const email = normalizeEmail(
      (typeof user?.Email === "string" && user.Email) ||
      (typeof user?.email === "string" && user.email) ||
      (typeof user?.get_email === "function" && user.get_email()) ||
      "",
    );

    if (!email) return;

    let name = "";
    let personalNumber = "Нет номера";

    if (typeof user?.get_metadata === "function") {
      const metadata = user.get_metadata();
      const properties = metadata?.UserProfileProperties || [];
      name =
        metadata?.DisplayName ||
        properties.find((item: any) => item.Key === "PreferredNameRus")?.Value ||
        properties.find((item: any) => item.Key === "PreferredName")?.Value ||
        "";
      personalNumber =
        metadata?.EmployeeNumber ||
        properties.find((item: any) => item.Key === "abiPersonnelNumber")?.Value ||
        properties.find((item: any) => item.Key === "UserName")?.Value ||
        "Нет номера";
    }

    if (!name) {
      name = nameFromEmail(email);
    } else if (name.split(" ").length === 3) {
      const [firstName, lastName] = name.split(" ");
      name = `${firstName} ${lastName}`;
    }

    meta.set(email, { name, personalNumber });
  });

  return meta;
}

async function getAutoSendParams() {
  const response = await getItemsRequest({
    listName: paramsListName,
    filters: { Key: ["audit", "auditDateDefault"] },
    rowLimit: 50,
  });

  const auditRepeat = Number(response.items.find((item: any) => item.Key === "audit")?.Value?.trim?.() || 0);
  const auditDays = Number(response.items.find((item: any) => item.Key === "auditDateDefault")?.Value?.trim?.() || 30);

  return { auditRepeat, auditDays };
}

async function removePendingNotifications(notificationIds: number[]) {
  if (!notificationIds.length) return;

  const emails = await getItemsRequest({
    listName: emailQueueListName,
    filters: { NotificationID: notificationIds },
    rowLimit: 4999,
  });

  const emailIds = emails.items.map((item: any) => ({ ID: item.ID }));

  if (emailIds.length > 0) {
    await deleteItemRequest({
      listName: emailQueueListName,
      ID: 0,
      Ids: emailIds,
    });
  }

  await deleteItemRequest({
    listName: notificationsCardListName,
    ID: 0,
    Ids: notificationIds.map((ID) => ({ ID })),
  });
}

async function sendNotificationsForUser({
  userEmail,
  userName,
  personalNumber,
  documents,
  auditDays,
  auditRepeat,
}: {
  userEmail: string;
  userName: string;
  personalNumber: string;
  documents: any[];
  auditDays: number;
  auditRepeat: number;
}) {
  for (const document of documents) {
    const reviewDate = moment().add(auditDays, "days").toDate();

    const notificationID = await createRawItem(notificationIdsListName, { Title: "Ознакомление" });

    const notificationListId = await createRawItem(notificationsCardListName, {
      Title: "Уведомление " + notificationID,
      NotificationID: notificationID,
      Employee: userEmail,
      EmployeeEmail: userEmail,
      Document: document.ID,
      DocumentCode: `${document.Code}.${document.Index}`,
      ReviewDate: moment(reviewDate).toISOString(),
      AuthorComment: "",
      NotificationType: notificationStatuses.familiarization,
      AuthorEmail: SYSTEM_AUTHOR_EMAIL,
    });

    if (!emailsSender) continue;

    const baseEmail = {
      Title: "Мои документы | Ознакомление с документами",
      Subject: "Мои документы | Ознакомление с документами",
      Recepients: userEmail,
      NotificationID: notificationListId,
    };

    const body = `Здравствуйте ${userName} (${personalNumber}),<br/><br/>Прочитайте следующий (-е) документ (-ы) и подтвердите факт ознакомления с документом (-ами):<br/><br/><a href="${siteUrl}/dashboard/1?documentCard=${document.ID}">${document.Title}</a><br/><br/>Срок ознакомления: <span>${moment(reviewDate).format("DD.MM.YYYY")}</span>`;

    await createRawItem(emailQueueListName, {
      ...baseEmail,
      Body: body,
      DateToBeSentAt: new Date().toISOString(),
      RepeatAt: 0,
      RepeatTimes: 0,
    });

    if (auditRepeat > 0 && !moment(reviewDate).isSame(new Date(), "day")) {
      const expiredBody = `Здравствуйте ${userName} (${personalNumber}),<br/><br/>Прочитайте следующий (-е) документ (-ы) и подтвердите факт ознакомления с документом (-ами):<br/><br/><a href="${siteUrl}/dashboard/1?documentCard=${document.ID}">${document.Title}</a><br/><br/>Срок ознакомления: <span style="color:red;">${moment(reviewDate).format("DD.MM.YYYY")}</span>`;

      await createRawItem(emailQueueListName, {
        ...baseEmail,
        Body: expiredBody,
        DateToBeSentAt: moment(reviewDate).toISOString(),
        RepeatAt: auditRepeat,
        RepeatTimes: 100000,
      });
    }
  }
}

export async function prepareWorkstationNewUsersContext<L extends ListFieldsKeys>({
  listName,
  body,
  bodyArr,
}: {
  listName: L;
  body?: Partial<Record<keyof ListFieldsType[L], any>>;
  bodyArr?: Partial<Record<keyof ListFieldsType[L], any>>[];
}): Promise<WorkstationSyncContext[]> {
  if (listName !== MDWorkstationListName) return [];

  const updates = (bodyArr && bodyArr.length > 0 ? bodyArr : body ? [body] : []).filter(
    (item: any) => typeof item?.ID === "number" && Array.isArray(item?.User),
  );

  if (!updates.length) return [];

  const contexts = await Promise.all(
    updates.map(async (item: any) => {
      const response = await getItemsRequest({
        listName: MDWorkstationListName,
        filters: { ID: item.ID },
        rowLimit: 1,
      });

      const currentWorkstation = response.items[0];
      const prevEmails = extractWorkstationUserEmails(currentWorkstation?.User);
      const nextEmails = extractWorkstationUserEmails(item.User);
      const addedUserEmails = nextEmails.filter((email) => !prevEmails.includes(email));

      return {
        workstationId: item.ID,
        addedUserEmails,
      };
    }),
  );

  return contexts.filter((item) => item.addedUserEmails.length > 0);
}

export function prepareAddedWorkstationUsersContext<L extends ListFieldsKeys>({
  listName,
  body,
  bodyArr,
  createdIds,
}: {
  listName: L;
  body?: Partial<Record<keyof ListFieldsType[L], any>>;
  bodyArr?: Partial<Record<keyof ListFieldsType[L], any>>[];
  createdIds: number[];
}): WorkstationSyncContext[] {
  if (listName !== MDWorkstationListName || !createdIds.length) return [];

  const createdItems = (bodyArr && bodyArr.length > 0 ? bodyArr : body ? [body] : []).filter(
    (item: any) => Array.isArray(item?.User),
  );

  return createdIds
    .map((id, idx) => ({
      workstationId: id,
      addedUserEmails: extractWorkstationUserEmails((createdItems[idx] as any)?.User),
    }))
    .filter((item) => item.addedUserEmails.length > 0);
}

export async function syncWorkstationNewUsersFamiliarization(contexts: WorkstationSyncContext[]) {
  if (!contexts.length) return;

  const { auditDays, auditRepeat } = await getAutoSendParams();

  for (const context of contexts) {
    const workstationResponse = await getItemsRequest({
      listName: MDWorkstationListName,
      filters: { ID: context.workstationId },
      rowLimit: 1,
    });

    const workstation = workstationResponse.items[0];
    const userMeta = extractUserMeta(workstation?.User);

    const documentsResponse = await getItemsRequest({
      listName: myDocumentsListName,
      filters: {
        WorkPlaces: [context.workstationId],
        Status: [docStatuses.public, docStatuses.review],
      },
      rowLimit: 4999,
    });

    const documents = documentsResponse.items || [];
    const documentIds = documents.map((item: any) => item.ID);

    if (!documentIds.length) continue;

    for (const userEmail of context.addedUserEmails) {
      const existingNotifications = await getItemsRequest({
        listName: notificationsCardListName,
        filters: {
          EmployeeEmail: userEmail,
          Document: documentIds,
          NotificationType: notificationStatuses.familiarization,
        },
        rowLimit: 4999,
      });

      const readDocuments = new Set<number>(
        existingNotifications.items
          .filter((item: any) => item.Read && !item.VersionDocument)
          .map((item: any) => getLookupId(item.Document))
          .filter((id: number | null): id is number => id !== null),
      );

      const pendingNotificationIds = existingNotifications.items
        .filter((item: any) => !item.Read && !String(item.Title || "").includes("Отклонено"))
        .map((item: any) => item.ID);

      if (pendingNotificationIds.length > 0) {
        await removePendingNotifications(pendingNotificationIds);
      }

      const documentsToNotify = documents.filter((item: any) => !readDocuments.has(item.ID));

      if (!documentsToNotify.length) continue;

      const meta = userMeta.get(userEmail) || {
        name: nameFromEmail(userEmail),
        personalNumber: "Нет номера",
      };

      await sendNotificationsForUser({
        userEmail,
        userName: meta.name,
        personalNumber: meta.personalNumber,
        documents: documentsToNotify,
        auditDays,
        auditRepeat,
      });
    }
  }
}
